import React, { useEffect, useCallback, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import InputAdornment from '@mui/material/InputAdornment';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useUsers } from '../../hooks/useUsers';
import { useRoles } from '../../hooks/useRoles';

type UserFormMode = 'create' | 'edit' | 'view';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (userId: string) => void;
  userId?: string;
  mode?: UserFormMode;
}

// FormData type will be inferred from the validation schema

const UserForm: React.FC<UserFormProps> = ({
  open,
  onClose,
  onSuccess,
  userId,
  mode = 'create',
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { 
    createUser, 
    updateUser, 
    users = [], 
    loading: usersLoading, 
    error: usersError 
  } = useUsers();
  const { roles = [], loading: rolesLoading, error: rolesError } = useRoles();
  
  const user = users.find(u => u.id === userId);
  const isNewUser = mode === 'create';
  const isEditMode = mode === 'edit';
  const isViewMode = mode === 'view' || (!isNewUser && !isEditMode);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(true);
  
  // Define form data type
  type FormData = {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    roleId: string;
    password?: string;
    confirmPassword?: string;
    isActive: boolean;
  };

  // Form validation schema
  const validationSchema = yup.object().shape({
    username: yup
      .string()
      .required('Username is required')
      .min(3, 'Username must be at least 3 characters')
      .max(30, 'Username must not exceed 30 characters')
      .matches(
        /^[a-zA-Z0-9_]+$/,
        'Username can only contain letters, numbers, and underscores'
      ),
    email: yup
      .string()
      .email('Please enter a valid email address')
      .required('Email is required')
      .max(100, 'Email must not exceed 100 characters'),
    firstName: yup
      .string()
      .required('First name is required')
      .max(50, 'First name must not exceed 50 characters'),
    lastName: yup
      .string()
      .required('Last name is required')
      .max(50, 'Last name must not exceed 50 characters'),
    phone: yup
      .string()
      .required('Phone number is required')
      .matches(
        /^[0-9+\-()\s]+$/,
        'Please enter a valid phone number'
      ),
    roleId: yup.string().required('Role is required'),
    isActive: yup.boolean(),
    ...(isNewUser ? {
      password: yup
        .string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
          'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
        ),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref('password'), undefined], 'Passwords must match')
        .required('Please confirm your password')
    } : {})
  });

  // Initialize form with react-hook-form
  const { 
    control, 
    handleSubmit, 
    formState: { errors, isSubmitting },
    reset,
    setValue,
    getValues,
    trigger,
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema) as any, // Type assertion to fix yup resolver type
    defaultValues: {
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      roleId: roles[0]?.id || '',
      password: '',
      confirmPassword: '',
      isActive: true,
    },
    mode: 'onChange',
  });
  
  // Watch password fields for validation
  const watchPassword = watch('password', '');
  const watchConfirmPassword = watch('confirmPassword', '');
  
  // Update local isActive state when form value changes
  const watchIsActive = watch('isActive', true);
  React.useEffect(() => {
    setIsActive(watchIsActive);
  }, [watchIsActive]);

  // Initialize form with user data when opened, mode changes, or user data changes
  useEffect(() => {
    if (!open) {
      // Reset form when dialog is closed
      reset({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        roleId: roles[0]?.id || '',
        password: '',
        confirmPassword: '',
        isActive: true,
      });
      setIsActive(true);
      setShowPasswordFields(false);
      return;
    }
    
    const initializeForm = async () => {
      if (user && (isEditMode || isViewMode)) {
        // Handle role ID - it might be a string or an object with an id
        let roleId = '';
        if (user.role) {
          roleId = typeof user.role === 'string' ? user.role : user.role.id || '';
        } else if (user.roleId) {
          roleId = user.roleId;
        } else if (roles.length > 0) {
          roleId = roles[0].id;
        }
        
        // Set form values from user data
        const formValues = {
          username: user.username || '',
          email: user.email || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          phone: user.phone || user.phoneNumber || '',
          roleId,
          password: '',
          confirmPassword: '',
          isActive: user.isActive !== false, // Default to true if undefined
        };
        
        reset(formValues);
        setIsActive(formValues.isActive);
        setShowPasswordFields(false);
      } else if (isNewUser) {
        // Reset form for new user
        const newFormValues = {
          username: '',
          email: '',
          firstName: '',
          lastName: '',
          phone: '',
          roleId: roles[0]?.id || '',
          password: '',
          confirmPassword: '',
          isActive: true,
        };
        reset(newFormValues);
        setIsActive(true);
        setShowPasswordFields(false);
      }
    };
    
    initializeForm();
  }, [open, mode, user, roles, isEditMode, isViewMode, isNewUser, reset, setValue]);

  // This effect is now handled by the initializeForm effect above

  const handleClose = useCallback(() => {
    if (isSubmitting) return; // Prevent closing while submitting
    
    if (isViewMode) {
      onClose();
      reset();
      return;
    }

    // Only show confirmation if there are unsaved changes
    const currentValues = getValues();
    const defaultFormValues = {
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      roleId: roles[0]?.id || '',
      password: '',
      confirmPassword: '',
      isActive: true,
    };
    
    const hasChanges = Object.keys(currentValues).some(
      key => currentValues[key as keyof FormData] !== defaultFormValues[key as keyof typeof defaultFormValues]
    );
    
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
        reset();
      }
    } else {
      onClose();
      reset();
    }
  }, [isViewMode, onClose, reset, getValues, roles, isSubmitting]);

  // Handle keyboard events
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  }, [handleClose]);

  const handleResetPassword = useCallback(async () => {
    if (!userId) {
      enqueueSnackbar('User ID is required', { variant: 'error' });
      return;
    }
    
    try {
      const confirm = window.confirm(
        `Are you sure you want to reset the password for ${user?.username || 'this user'}? ` +
        'They will receive an email with instructions to set a new password.'
      );
      
      if (!confirm) return;
      
      setShowPasswordFields(true);
      setValue('password', '');
      setValue('confirmPassword', '');
      
      // In a real implementation, this would call an API endpoint
      // to send a password reset email to the user
      // await userApi.requestPasswordReset(userId);
      
      enqueueSnackbar('Password reset email has been sent', { variant: 'success' });
    } catch (error) {
      console.error('Error resetting password:', error);
      enqueueSnackbar('Failed to reset password. Please try again.', { variant: 'error' });
    }
  }, [enqueueSnackbar, userId, setValue, user?.username]);

  const onSubmit = useCallback(async (formData: FormData) => {
    try {
      console.log('Form submitted with data:', formData);
      
      // Validate role ID
      if (!formData.roleId) {
        throw new Error('Please select a valid role');
      }
      
      // Prepare user data for submission
      const userData: any = {
        username: formData.username.trim(),
        email: formData.email.trim().toLowerCase(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        roleId: formData.roleId,
        isActive: Boolean(formData.isActive),
      };

      // Include password if provided and we're either creating a new user or updating with a new password
      if ((mode === 'create' || showPasswordFields) && formData.password) {
        userData.password = String(formData.password);
      }
      
      console.log('Submitting user data:', userData);

      if (mode === 'create') {
        const newUser = await createUser(userData);
        enqueueSnackbar('User created successfully', { variant: 'success' });
        onClose();
        if (onSuccess) onSuccess(newUser.id);
      } else if (user) {
        await updateUser(user.id, userData);
        enqueueSnackbar('User updated successfully', { variant: 'success' });
        onClose();
      }
    } catch (error: any) {
      console.error('Error saving user:', error);
      enqueueSnackbar(
        error.message || 'Failed to save user',
        { variant: 'error' as const }
      );
    }
  }, [mode, showPasswordFields, createUser, updateUser, enqueueSnackbar, onClose, onSuccess, user]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth onKeyDown={handleKeyDown}>
      <form onSubmit={handleSubmit((data) => onSubmit(data as FormData))} noValidate>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="div">
              {mode === 'view' ? 'View User' : mode === 'edit' ? 'Edit User' : 'Create New User'}
            </Typography>
            <IconButton 
              onClick={handleClose} 
              color="inherit"
              disabled={isSubmitting}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      
        <DialogContent dividers>

          {usersLoading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {/* Username Field */}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="username"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Username"
                        fullWidth
                        margin="normal"
                        error={!!errors.username}
                        helperText={errors.username?.message}
                        disabled={isViewMode || isSubmitting}
                        InputProps={{
                          readOnly: isViewMode,
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Email Field */}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="email"
                        label="Email"
                        fullWidth
                        margin="normal"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        disabled={isViewMode || isSubmitting}
                        InputProps={{
                          readOnly: isViewMode,
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* First Name Field */}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="First Name"
                        fullWidth
                        margin="normal"
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        disabled={isViewMode || isSubmitting}
                        InputProps={{
                          readOnly: isViewMode,
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Last Name Field */}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Last Name"
                        fullWidth
                        margin="normal"
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        disabled={isViewMode || isSubmitting}
                        InputProps={{
                          readOnly: isViewMode,
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Phone Field */}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Phone"
                        fullWidth
                        margin="normal"
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                        disabled={isViewMode || isSubmitting}
                        InputProps={{
                          readOnly: isViewMode,
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Role Field */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal" error={!!errors.roleId}>
                    <InputLabel id="role-select-label">Role</InputLabel>
                    <Controller
                      name="roleId"
                      control={control}
                      render={({ field: { value, onChange, ...field } }) => {
                        // Find the selected role
                        const selectedRole = roles.find(r => r.id === value);
                        const roleName = selectedRole?.name || 
                                        (value ? 
                                          value.split('_').map(w => 
                                            w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
                                          ).join(' ') : 'Not assigned');
                        
                        // For view mode, show a read-only field
                        if (isViewMode) {
                          return (
                            <TextField
                              value={roleName}
                              label="Role"
                              fullWidth
                              margin="normal"
                              disabled
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                          );
                        }
                        
                        // For edit mode, show a select dropdown
                        return (
                          <Select
                            {...field}
                            labelId="role-select-label"
                            label="Role"
                            value={value || ''}
                            onChange={(e) => {
                              console.log('Role changed to:', e.target.value);
                              onChange(e);
                            }}
                            disabled={isViewMode || (!isNewUser && !!user?.isSystem) || isSubmitting}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select role' }}
                            renderValue={(selected) => {
                              if (!selected) return <em>Select a role</em>;
                              const role = roles.find(r => r.id === selected);
                              return role?.name || selected;
                            }}
                          >
                            <MenuItem value="" disabled>
                              <em>Select a role</em>
                            </MenuItem>
                            {roles.map((role) => (
                              <MenuItem key={role.id} value={role.id}>
                                {role.name}
                              </MenuItem>
                            ))}
                          </Select>
                        );
                      }}
                    />
                    {errors.roleId && (
                      <FormHelperText>{errors.roleId.message}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {!isNewUser && (
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                      <Controller
                        name="isActive"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isActive}
                                onChange={(e) => {
                                  const newValue = e.target.checked;
                                  console.log('Toggling isActive to:', newValue);
                                  setIsActive(newValue);
                                  field.onChange(newValue);
                                  // Trigger validation if needed
                                  trigger('isActive');
                                }}
                                onBlur={field.onBlur}
                                name={field.name}
                                inputRef={field.ref}
                                color="primary"
                                disabled={isViewMode || isSubmitting}
                              />
                            }
                            label={
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Box
                                  width={10}
                                  height={10}
                                  borderRadius="50%"
                                  bgcolor={field.value ? 'success.main' : 'error.main'}
                                />
                                <Typography variant="body2">
                                  {field.value ? 'Active' : 'Inactive'}
                                </Typography>
                              </Stack>
                            }
                            labelPlacement="start"
                            sx={{
                              m: 0,
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}
              </Grid>

              {(isNewUser || showPasswordFields) && !isViewMode && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle1" gutterBottom>
                      Set Password
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="password"
                          label="Password"
                          fullWidth
                          margin="normal"
                          error={!!errors.password}
                          helperText={errors.password?.message}
                          disabled={isSubmitting}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <VpnKeyIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="confirmPassword"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="password"
                          label="Confirm Password"
                          fullWidth
                          margin="normal"
                          error={!!errors.confirmPassword}
                          helperText={errors.confirmPassword?.message}
                          disabled={isSubmitting}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              {isEditMode && !showPasswordFields && !isViewMode && (
                <Button
                  onClick={handleResetPassword}
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ mr: 1 }}
                >
                  Reset Password
                </Button>
              )}
            </Box>
            <Box>
              <Button
                onClick={handleClose}
                color="inherit"
                disabled={isSubmitting}
                sx={{ mr: 1 }}
              >
                {isViewMode ? 'Close' : 'Cancel'}
              </Button>
              {!isViewMode && (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || (showPasswordFields && (!watchPassword || !watchConfirmPassword))}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              )}
            </Box>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserForm;
